/**
 * HVACStamps - Panel for selecting and placing HVAC markup stamps
 */

import React, { useState } from 'react';
import { STAMP_CATEGORIES, getStampById, STAMP_SIZE } from '../../constants/hvacStamps';

/**
 * Stamp selector panel - shows available stamps for placement
 */
export default function HVACStamps({
  onSelectStamp,
  selectedStamp,
  onClose,
  isMobile = false,
}) {
  const [expandedCategory, setExpandedCategory] = useState('status');

  const renderStampPreview = (stamp) => {
    const lines = stamp.label.split('\n');
    return (
      <button
        key={stamp.id}
        onClick={() => onSelectStamp(stamp.id)}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '8px 12px',
          minWidth: '70px',
          minHeight: '45px',
          background: stamp.color,
          border: selectedStamp === stamp.id ? '2px solid #fff' : '2px solid transparent',
          borderRadius: '6px',
          cursor: 'pointer',
          transition: 'all 0.15s ease',
          boxShadow: selectedStamp === stamp.id ? '0 0 8px rgba(255,255,255,0.5)' : 'none',
        }}
        title={stamp.label.replace('\n', ' ')}
      >
        {lines.map((line, i) => (
          <span
            key={i}
            style={{
              color: stamp.textColor,
              fontSize: lines.length > 1 ? '9px' : '10px',
              fontWeight: '700',
              lineHeight: 1.2,
              textAlign: 'center',
              whiteSpace: 'nowrap',
            }}
          >
            {line}
          </span>
        ))}
      </button>
    );
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: isMobile ? 'flex-end' : 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: isMobile ? 0 : '20px',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          backgroundColor: '#1a2332',
          borderRadius: isMobile ? '12px 12px 0 0' : '12px',
          width: isMobile ? '100%' : '380px',
          maxHeight: '90vh',
          overflow: 'auto',
          border: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
          background: 'rgba(0,200,255,0.05)',
        }}>
          <span style={{ color: '#00c8ff', fontWeight: '600', fontSize: '14px' }}>
            HVAC Stamps
          </span>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,100,100,0.15)',
              border: 'none',
              color: '#ff6666',
              fontSize: '16px',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '4px',
              lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '16px 20px' }}>
          <p style={{ color: '#8090a0', fontSize: '12px', marginBottom: '16px' }}>
            Select a stamp, then click on the floor plan to place it.
          </p>

          {STAMP_CATEGORIES.map((category) => (
            <div key={category.id} style={{ marginBottom: '16px' }}>
              <button
                onClick={() => setExpandedCategory(expandedCategory === category.id ? null : category.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  padding: '8px 12px',
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '6px',
                  color: '#d0d8e0',
                  fontSize: '12px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginBottom: expandedCategory === category.id ? '12px' : 0,
                }}
              >
                <span>{category.label}</span>
                <span style={{ fontSize: '10px' }}>
                  {expandedCategory === category.id ? '▼' : '▶'}
                </span>
              </button>

              {expandedCategory === category.id && (
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  padding: '4px',
                }}>
                  {category.stamps.map(renderStampPreview)}
                </div>
              )}
            </div>
          ))}

          {/* Instructions */}
          <div style={{
            marginTop: '16px',
            padding: '12px',
            background: 'rgba(0,0,0,0.2)',
            borderRadius: '8px',
            fontSize: '11px',
            color: '#6080a0',
          }}>
            <div style={{ fontWeight: '600', marginBottom: '6px' }}>Tips:</div>
            <ul style={{ margin: 0, paddingLeft: '16px' }}>
              <li>Click stamp to select, then click on plan to place</li>
              <li>Drag placed stamps to reposition</li>
              <li>Click a placed stamp to select and edit/delete</li>
              <li>Use R key to rotate selected stamp</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Stamp editor - appears when a placed stamp is selected
 * Non-blocking panel that allows canvas interaction for dragging
 */
export function StampEditor({
  stamp,
  onUpdate,
  onDelete,
  onClose,
  isMobile = false,
}) {
  const stampDef = getStampById(stamp.stampType);

  if (!stampDef) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: isMobile ? 0 : '20px',
        right: isMobile ? 0 : '20px',
        left: isMobile ? 0 : 'auto',
        backgroundColor: '#1a2332',
        borderRadius: isMobile ? '12px 12px 0 0' : '12px',
        width: isMobile ? '100%' : '240px',
        border: '1px solid rgba(255,255,255,0.15)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
        zIndex: 1000,
        padding: '12px',
      }}
    >
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '10px',
      }}>
        <span style={{ color: '#00c8ff', fontSize: '11px', fontWeight: '600' }}>
          Edit Stamp
        </span>
        <button
          onClick={onClose}
          style={{
            background: 'rgba(255,100,100,0.15)',
            border: 'none',
            color: '#ff6666',
            cursor: 'pointer',
            fontSize: '14px',
            padding: '2px 6px',
            borderRadius: '4px',
            lineHeight: 1,
          }}
        >
          ×
        </button>
      </div>

      {/* Stamp preview */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '8px',
        background: stampDef.color,
        borderRadius: '4px',
        marginBottom: '10px',
      }}>
        <span style={{
          color: stampDef.textColor,
          fontSize: '10px',
          fontWeight: '700',
          textAlign: 'center',
          whiteSpace: 'pre-line',
        }}>
          {stampDef.label}
        </span>
      </div>

      {/* Drag hint */}
      <p style={{
        color: '#6080a0',
        fontSize: '10px',
        marginBottom: '10px',
        textAlign: 'center',
      }}>
        Drag stamp on canvas to move
      </p>

      {/* Rotation control */}
      <div style={{ marginBottom: '10px' }}>
        <label style={{ color: '#8090a0', fontSize: '10px', display: 'block', marginBottom: '4px' }}>
          Rotation: {stamp.rotation}°
        </label>
        <input
          type="range"
          min="0"
          max="360"
          step="15"
          value={stamp.rotation}
          onChange={(e) => onUpdate({ ...stamp, rotation: parseInt(e.target.value) })}
          style={{ width: '100%', accentColor: '#00c8ff' }}
        />
      </div>

      {/* Quick rotation buttons */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '10px' }}>
        {[0, 90, 180, 270].map((angle) => (
          <button
            key={angle}
            onClick={() => onUpdate({ ...stamp, rotation: angle })}
            style={{
              flex: 1,
              padding: '4px',
              background: stamp.rotation === angle ? 'rgba(0,200,255,0.2)' : 'rgba(255,255,255,0.05)',
              border: stamp.rotation === angle ? '1px solid rgba(0,200,255,0.4)' : '1px solid rgba(255,255,255,0.1)',
              borderRadius: '4px',
              color: stamp.rotation === angle ? '#00c8ff' : '#8090a0',
              fontSize: '10px',
              cursor: 'pointer',
            }}
          >
            {angle}°
          </button>
        ))}
      </div>

      {/* Delete button */}
      <button
        onClick={onDelete}
        style={{
          width: '100%',
          padding: '8px',
          background: 'rgba(244, 67, 54, 0.15)',
          border: '1px solid rgba(244, 67, 54, 0.3)',
          borderRadius: '6px',
          color: '#f44336',
          fontSize: '11px',
          fontWeight: '600',
          cursor: 'pointer',
        }}
      >
        Delete Stamp
      </button>
    </div>
  );
}
